import { ApiResponse, GlobalStore } from '@roc/feature-app-core';
import { GENERIC_ERROR_MESSAGE } from '@roc/feature-utils';
import { InsuranceInformationReviewInternalService } from '../services/insuranceInformationReviewInternalService';
import { InsuranceInformationReviewBaseStore } from './insuranceInformationReviewBaseStore';
import { InsuranceInformationBaseStore } from './insuranceInformationBaseStore';
import { LogInternalService } from 'apps/roc-internal-portal/src/app/services/logInternalService';
import { flow, makeObservable } from 'mobx';
import { AIDocType } from '@roc/feature-documents';

export class InsuranceInformationReviewInternalStore extends InsuranceInformationReviewBaseStore {
  insuranceInformationReviewService: InsuranceInformationReviewInternalService;

  constructor(
    globalStore: GlobalStore,
    insuranceInformationStore: InsuranceInformationBaseStore
  ) {
    super(globalStore, insuranceInformationStore);
    this.insuranceInformationReviewService = new InsuranceInformationReviewInternalService();
    this.logService = new LogInternalService();
    makeObservable(this, {
      logEvents: flow,
      logClearFlagsEvents: flow,
      logManualSendForRevisionsEvents: flow,
    });
  }

  *getInsuranceQuotes(loanId) {
    try {
      const response: ApiResponse = yield this.insuranceInformationReviewService.getInsuranceQuotes(
        loanId
      );
      const quotes = response?.data ?? [];
      this.quotes = quotes.filter(quote => !quote?.quoteShowId?.includes('Elmsure'));
      this.quotesOptions = this.quotes.map(quote => ({
        label: quote.quoteShowId,
        value: quote.insuranceQuoteDetailId,
      }));
    } catch (error) {
      console.log('Error occurred on getInsuranceQuotes', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *getInsuranceQuotesAndSetSelected(loanId) {
    try {
      yield this.getInsuranceQuotes(loanId);
      if (this.quotes.length) {
        this.setSelectedQuote(this.quotesOptions[0].value);
      }
    } catch (error) {
      console.log('Error occurred on getInsuranceQuotes', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *getInsuranceReviewData(quoteId: string) {
    try {
      this.insuranceReviewData = [];
      this.insuranceQuoteDataId = null;
      const response: ApiResponse = yield this.insuranceInformationReviewService.getInsuranceReviewData(
        quoteId
      );
      const data = response?.data;
      this.insuranceReviewData = this.normalizeAutomatedReviewItems(data.sectionData);
      this.insuranceQuoteDataId = data.insuranceQuoteDataId;
      this.insuranceAutomationStatus = data.automationStatus
      this.automatedSectionFlags = data.automatedSectionFlags;
      this.previewSectionFlags = data.previewSectionFlags;
      this.showPreview = data.showPreview;
      this.sectionData = data.sectionData;
      this.automatedDocumentsData = data.automatedDocumentsData;
      this.readOnlyPackageSection = data.readOnlyPackageSection;
      this.sectionDataAutomated = data.sectionDataAutomated;

    } catch (error) {
      console.log('Error occurred while getting the data', error);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  * sendPreviewDetails(action: string, quoteId: any) {
    try {
      const data = {
        action,
        quoteId,
        data: this.previewDataReplacementList,
      };
      yield this.insuranceInformationReviewService.sendPreviewDetails(data);
    } catch (error) {
      console.error('Error sending preview:', error);
    }
  }

  *saveData() {
    yield this.logEvents();
    try {
      yield this.insuranceInformationReviewService.saveData(
        this.insuranceQuoteDataId,
        this.insuranceReviewData
      );
    } catch (e) {
      console.error('Error on saveData', e);
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *saveClearFlagButtonLogInfo() {
    try {
      yield this.logClearFlagsEvents();
    } catch (e) {
      console.error('Error on savingClearflag button info', e);
    }
  }

  *changeQuoteStatus(quoteId: any, status: string) {
    try {
      this.insuranceInformationReviewService.changeQuoteStatus(quoteId, status);
    } catch (e) {
      this.globalStore.notificationStore.showErrorNotification({
        message: GENERIC_ERROR_MESSAGE,
      });
    }
  }

  *createResolutions(data: any) {
    this.addManualSendForRevisionsEvent();
    yield this.logManualSendForRevisionsEvents();
    yield this.logEvents();
    yield this.insuranceInformationReviewService.createResolutions(data);
  }

  *getRevisionText(request: any) {
    return yield this.insuranceInformationReviewService.getRevisionText(
      request
    );
  }

  private getTypeKey(type: AIDocType) {
    return Object.keys(AIDocType).find(key => AIDocType[key] === type)
  }

  *logEvents() {
    try {
      yield this.logService.logEventInsuranceReview([...this.eventList.values()], this.insuranceQuoteDataId);
      this.eventList = new Map();
    } catch (e) {
      console.error('Error logging events', e);
    }
  }

  *logManualSendForRevisionsEvents() {
    try {
      yield this.logService.logEvent(this.manualSendForRevisionsEventObject);
      this.manualSendForRevisionsEventObject = {};
    } catch (e) {
      console.error('Error logging manualSendForRevisions events', e);
    }
  }

  *logClearFlagsEvents() {
    try {
      yield this.logService.logEvent(this.clearFlagsEventObject);
      this.clearFlagsEventObject = {};
    } catch (e) {
      console.error('Error logging clear flags events', e);
    }
  }

  *updateAutoRevisionForQuote(quoteId: any, value: any) {
    try {
      return yield this.insuranceInformationReviewService.updateAutomatedResolutionsForQuote(quoteId, value);
    } catch (e) {
      console.error(`Error while updating the automated resolution value for quote id: ${quoteId}`, e);
    }
  }
}
